import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2 } from "lucide-react";

interface Measurement {
  id: string;
  measured_at: string;
  weight_lbs: number | null;
  body_fat_percent: number | null;
  chest_inches: number | null;
  waist_inches: number | null;
  hips_inches: number | null;
  left_arm_inches: number | null;
  right_arm_inches: number | null;
  left_thigh_inches: number | null;
  right_thigh_inches: number | null;
  left_calf_inches: number | null;
  right_calf_inches: number | null;
  neck_inches: number | null;
  notes: string | null;
}

interface MeasurementHistoryTableProps {
  measurements: Measurement[];
  onEdit?: (measurement: Measurement) => void;
  onDelete?: (measurementId: string) => void;
  showActions?: boolean;
}

const MeasurementHistoryTable = ({
  measurements,
  onEdit,
  onDelete,
  showActions = true,
}: MeasurementHistoryTableProps) => {
  const formatValue = (value: number | null, unit: string) => {
    if (value === null) return "-";
    return `${value}${unit}`;
  };

  const calculateChange = (
    current: number | null,
    previous: number | null
  ): { value: number; direction: "up" | "down" | "same" } | null => {
    if (current === null || previous === null) return null;
    const change = current - previous;
    if (change === 0) return { value: 0, direction: "same" };
    return {
      value: Math.abs(change),
      direction: change > 0 ? "up" : "down",
    };
  };

  if (measurements.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No measurements recorded yet
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Weight</TableHead>
            <TableHead className="text-right">Body Fat</TableHead>
            <TableHead className="text-right">Waist</TableHead>
            <TableHead className="text-right">Chest</TableHead>
            <TableHead className="text-right">Hips</TableHead>
            {showActions && <TableHead className="w-[100px]"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {measurements.map((measurement, index) => {
            const previousMeasurement = measurements[index + 1];
            const weightChange = calculateChange(
              measurement.weight_lbs,
              previousMeasurement?.weight_lbs
            );
            const bfChange = calculateChange(
              measurement.body_fat_percent,
              previousMeasurement?.body_fat_percent
            );
            const waistChange = calculateChange(
              measurement.waist_inches,
              previousMeasurement?.waist_inches
            );

            return (
              <TableRow key={measurement.id}>
                <TableCell className="font-medium">
                  {format(new Date(measurement.measured_at), "MMM d, yyyy")}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {formatValue(measurement.weight_lbs, " lbs")}
                    {weightChange && weightChange.direction !== "same" && (
                      <Badge
                        variant={
                          weightChange.direction === "down"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs px-1"
                      >
                        {weightChange.direction === "down" ? "-" : "+"}
                        {weightChange.value.toFixed(1)}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {formatValue(measurement.body_fat_percent, "%")}
                    {bfChange && bfChange.direction !== "same" && (
                      <Badge
                        variant={
                          bfChange.direction === "down" ? "default" : "secondary"
                        }
                        className="text-xs px-1"
                      >
                        {bfChange.direction === "down" ? "-" : "+"}
                        {bfChange.value.toFixed(1)}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {formatValue(measurement.waist_inches, '"')}
                    {waistChange && waistChange.direction !== "same" && (
                      <Badge
                        variant={
                          waistChange.direction === "down"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs px-1"
                      >
                        {waistChange.direction === "down" ? "-" : "+"}
                        {waistChange.value.toFixed(1)}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {formatValue(measurement.chest_inches, '"')}
                </TableCell>
                <TableCell className="text-right">
                  {formatValue(measurement.hips_inches, '"')}
                </TableCell>
                {showActions && (
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(measurement)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(measurement.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default MeasurementHistoryTable;
